import React, { createRef, useState } from 'react';
import PropTypes from 'prop-types';

import round from 'lodash/round';

import ActionsMenu from 'components/actions_menu';
import Modal from './modal';
import Tooltip from 'components/tooltip';
import confirm from 'components/confirm';
import { USD } from 'helpers/formatters/number_formats';
import { deleteDefaultDeduction } from './api';
import { transactionSideOptions } from './utils';

export default function DefaultDeduction({ categories, defaultDeduction, features, options, onChange, onDelete }) {
  const refs = {
    description: createRef(),
    rules: createRef(),
  };

  const [showModal, setShowModal] = useState(false);

  async function deleteDefault() {
    try {
      await confirm('Are you sure you want to delete this default deduction?', {
        title: 'Delete Default Deduction',
      });
    } catch {
      return;
    }

    deleteDefaultDeduction(defaultDeduction);
    onDelete(defaultDeduction);
  }

  let rules;
  if (defaultDeduction.dealTypes.length > 0) {
    rules = defaultDeduction.dealTypes
      .map(dealType => options.dealType.find(option => option.value === dealType).label)
      .join(', ');
  } else {
    rules = 'All Deals';
  }

  let formattedValue;
  if (defaultDeduction.value != null) {
    switch (defaultDeduction.unit) {
    case 'unit_dollars':
      formattedValue = USD.format(defaultDeduction.value);
      break;
    case 'unit_percent':
      formattedValue = `${round(defaultDeduction.value, 2)}%`;
      break;
    }
  }

  let tierLabel;
  if (features.tieredDeductions) {
    const tier = defaultDeduction.tier ||
      categories.find(category => category.name === defaultDeduction.category)?.tier;

    if (tier) {
      tierLabel = `(Tier ${tier})`;
    }
  }

  return (
    <tr>
      <td>{defaultDeduction.category}</td>
      <td>
        <Tooltip childrenRef={refs.description} content={defaultDeduction.description} onlyOnOverflow>
          <span ref={refs.description}>{defaultDeduction.description}</span>
        </Tooltip>
      </td>
      <td>Pre-Split {tierLabel}</td>
      {features.transactionSideDetails && (
        <td>
          {defaultDeduction.transactionSide
            ? transactionSideOptions.find(option => option.value === defaultDeduction.transactionSide).label
            : ''}
        </td>
      )}
      <td>
        <Tooltip childrenRef={refs.rules} content={rules} onlyOnOverflow>
          <span ref={refs.rules}>{rules}</span>
        </Tooltip>
      </td>
      <td>{formattedValue}</td>
      <td>
        <ActionsMenu
          id={defaultDeduction.id}
          onDelete={deleteDefault}
          onShowModal={() => setShowModal(true)}
        />
      </td>

      <Modal
        categories={categories}
        defaultDeduction={defaultDeduction}
        features={features}
        options={options}
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={onChange}
      />
    </tr>
  );
}

DefaultDeduction.propTypes = {
  categories: PropTypes.array,
  defaultDeduction: PropTypes.shape({
    transactionSide: PropTypes.string,
    unit: PropTypes.string,
    value: PropTypes.number,
  }).isRequired,
  features: PropTypes.shape({
    tieredDeductions: PropTypes.bool,
    transactionSideDetails: PropTypes.bool,
  }).isRequired,
  options: PropTypes.shape({
    dealType: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
    })).isRequired,
  }).isRequired,
  path: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
